import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Spacer } from "@aviary";
import { GoogleSSOButton } from "@shared/components";
import { AnalyticsEvent, useAnalyticsLogger, useFlippers } from "@shared/hooks";
import { useLocation, useNavigate, useParams } from "@shared/react-router-dom";
import {
  NewToFullscriptSignUp,
  SignInForm,
  SignInWrapper,
  SSOErrorBox,
  FormWrapper,
} from "@unauthenticated/shared/components";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import {
  StoreOffersPill,
  StoreLogo,
  SignUpModalContainer,
  SignUpModalHeader,
  SignUpModalTitle,
  SignUpModalSubtitle,
  SignUpModalFooter,
} from "@unauthenticated/signupSignin/components";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { useTreatmentPlanTerm } from "@unauthenticated/signupSignin/hooks/useTreatmentPlanTerm";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  ssoErrors?: string;
  hideExistingAccountCheck?: boolean;
}

const RxSignInModalMain: FC<Props> = ({
  patientAttributes,
  additionalAttributes,
  ssoErrors,
  hideExistingAccountCheck,
}) => {
  const treatmentPlanTerm = useTreatmentPlanTerm();
  const { t } = useTranslation();
  const {
    storeLogo,
    storeName,
    pracName,
    firstName,
    treatmentPlanSlug,
    uuid,
    labRecommendationId,
    storeSlug,
  } = patientAttributes;
  const queryParams = new URLSearchParams(useLocation().search);

  // TODO: Ryan - Remove the ignore once the react-router v6 upgrade is completed!
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { practitionerSlug, tpSlug } = useParams<"practitionerSlug" | "tpSlug">();
  const navigate = useNavigate();
  const searchParams = useLocation().search;
  const logAnalyticsEvent = useAnalyticsLogger();

  const renderSSOErrors = () => {
    if (ssoErrors === "rx_email_mismatch") {
      return (
        <SSOErrorBox
          isColor="danger"
          errorMessage={t(l.patientRxAuth.SSOEmailNotAssociated, { treatmentPlanTerm })}
          hideSubErrorMessage
        />
      );
    }
  };

  const signupModalSubtitle = labRecommendationId
    ? t(l.patientRxAuth.SignInWelcomeMessage, {
        firstName,
        pracName,
      })
    : t(l.patientRxAuth.SignInWelcomeMessage, {
        firstName,
        treatmentPlanTerm,
        pracName,
      });

  const handleOnSignUpClick = () => {
    navigate(`${authRoutes.welcome_sign_up(storeSlug)}${searchParams}`);
  };

  const [isPatientStoreLandingPageEnabled, isPatientSignupAnalyticsEnabled] = useFlippers(
    "titan_patient_store_landing_page",
    "titan_patient_signup_analytics"
  );

  const logMixpanelSignInEvent = () => {
    if (!isPatientSignupAnalyticsEnabled) return;
    logAnalyticsEvent({
      eventName: AnalyticsEvent.PATIENT_RX_SIGNIN,
      payload: {
        contains_store_logo: !!storeLogo,
        form_version: isPatientStoreLandingPageEnabled ? "StoreSignupPage" : "LegacyRxSignupPage",
        signin_method: "continue_with_email",
        has_treatment_plan: !!treatmentPlanSlug,
        utm_campaign: additionalAttributes?.utmCampaign ?? queryParams.get("utm_campaign") ?? "",
        utm_source: additionalAttributes?.utmSource ?? queryParams.get("utm_source") ?? "",
        utm_medium: additionalAttributes?.utmMedium ?? queryParams.get("utm_medium") ?? "",
      },
    });
  };

  const onCompleted = completedData => {
    const { errors, redirectPath } = completedData?.auth?.userSignIn;

    if (!errors?.message) logMixpanelSignInEvent();

    if (redirectPath) {
      window.location.assign(completedData.auth.userSignIn.redirectPath);
    }
  };

  return (
    <>
      <SignUpModalHeader>
        <StoreLogo storeLogo={storeLogo} storeName={storeName} />
        <SignUpModalTitle title={storeName} />
        <StoreOffersPill patientAttributes={patientAttributes} />
        {!isPatientStoreLandingPageEnabled && (
          <SignUpModalSubtitle subtitle={signupModalSubtitle} />
        )}
      </SignUpModalHeader>
      <SignUpModalContainer isNoPaddingTop={isPatientStoreLandingPageEnabled}>
        <FormWrapper>
          <SignInWrapper>
            {ssoErrors && renderSSOErrors()}
            <GoogleSSOButton
              isOrTextRendered
              signUpPage="rx_patient_sign_in_page"
              pageVersion="react_with_sso"
              treatmentPlanSlug={treatmentPlanSlug}
              labRecommendationId={labRecommendationId}
              storeSlug={storeSlug}
              uuid={uuid}
            />
            <SignInForm
              additionalAttributes={additionalAttributes}
              forgotPasswordLink={
                isPatientStoreLandingPageEnabled
                  ? authRoutes.welcome_forgot_password(storeSlug, uuid || treatmentPlanSlug)
                  : authRoutes.rx_sign_in_forgot_password(practitionerSlug, tpSlug)
              }
              onCompleted={onCompleted}
              hideExistingAccountCheck={hideExistingAccountCheck}
            />
            <Spacer height="doubleHalf" />
            <NewToFullscriptSignUp
              userType="patient"
              customSignUpAction={isPatientStoreLandingPageEnabled && handleOnSignUpClick}
            />
          </SignInWrapper>
        </FormWrapper>
      </SignUpModalContainer>
      {!isPatientStoreLandingPageEnabled && <SignUpModalFooter />}
    </>
  );
};

export { RxSignInModalMain };
